import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { format, addDays, parseISO } from 'date-fns'
import { ru } from 'date-fns/locale'
import Button from '@/components/ui/Button'
import { getTrainerSlots, AvailableSlot, getMyTrainers, Trainer, TrialBookingInfo } from '@/services/clients'
import { createBooking } from '@/services/bookings'

export default function ClientBooking() {
  const { trainerId } = useParams()
  const navigate = useNavigate()

  const [trainer, setTrainer] = useState<Trainer | null>(null)
  const [selectedDate, setSelectedDate] = useState<string | null>(null)
  const [selectedSlot, setSelectedSlot] = useState<AvailableSlot | null>(null)
  const [slots, setSlots] = useState<AvailableSlot[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isBooking, setIsBooking] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [actualTrainerId, setActualTrainerId] = useState<string | null>(trainerId || null)
  const [trialInfo, setTrialInfo] = useState<TrialBookingInfo | null>(null)

  const isTrial = trainer?.status === 'trial'

  // Генерируем даты на 2 недели вперед
  const dates = Array.from({ length: 14 }, (_, i) => addDays(new Date(), i))

  // Загрузка данных тренера (и автовыбор если trainerId не указан)
  useEffect(() => {
    const loadTrainer = async () => {
      try {
        const data = await getMyTrainers()
        // Активные тренеры - approved, trial или frozen (frozen может записаться и авто-разморозиться)
        const activeTrainers = data.trainers.filter(
          (t) => t.status === 'approved' || t.status === 'trial' || t.status === 'frozen'
        )

        if (trainerId) {
          // Если trainerId указан - ищем конкретного тренера
          const found = activeTrainers.find((t) => t.id === trainerId)
          if (found) {
            setTrainer(found)
            setActualTrainerId(found.id)
          }
        } else if (activeTrainers.length > 0) {
          // Если trainerId не указан - берём первого активного тренера
          setTrainer(activeTrainers[0])
          setActualTrainerId(activeTrainers[0].id)
        } else {
          setIsLoading(false)
        }
      } catch (err) {
        console.error('Error loading trainer:', err)
        setIsLoading(false)
      }
    }
    loadTrainer()
  }, [trainerId])

  // Загрузка слотов
  useEffect(() => {
    if (!actualTrainerId) {
      return
    }

    const loadSlots = async () => {
      setIsLoading(true)
      setError(null)
      try {
        const dateFrom = format(dates[0], 'yyyy-MM-dd')
        const dateTo = format(dates[dates.length - 1], 'yyyy-MM-dd')
        const data = await getTrainerSlots(actualTrainerId, dateFrom, dateTo)
        setSlots(data.slots)
        setTrialInfo(data.trial_booking_info)
      } catch (err: any) {
        console.error('Error loading slots:', err)
        setError(err.response?.data?.detail || 'Ошибка загрузки расписания')
      } finally {
        setIsLoading(false)
      }
    }
    loadSlots()
  }, [actualTrainerId])

  // Слоты на выбранную дату
  const slotsForDate = selectedDate
    ? slots.filter((slot) => slot.date === selectedDate)
    : []

  // Можно ли показывать выбор слотов (trial с ограничениями не видит)
  const canShowSlots = !isTrial || !trialInfo || trialInfo.can_book

  // Обработка бронирования
  const handleBooking = async () => {
    if (!selectedSlot) return

    setIsBooking(true)
    try {
      await createBooking(selectedSlot.id)
      alert('Вы успешно записались на тренировку!')
      navigate('/')
    } catch (err: any) {
      console.error('Error creating booking:', err)
      alert(err.response?.data?.detail || 'Ошибка бронирования')
    } finally {
      setIsBooking(false)
    }
  }

  return (
    <div className="p-4">
      <h1 className="text-2xl font-bold text-tg-text mb-4">Выбрать время</h1>

      {/* Информация о тренере */}
      {trainer && (
        <div className="bg-tg-secondary rounded-xl p-4 mb-6 flex items-center gap-4">
          <div className="w-14 h-14 bg-tg-button/20 rounded-full flex items-center justify-center text-2xl">
            💪
          </div>
          <div>
            <div className="font-medium text-tg-text">
              {trainer.user.first_name} {trainer.user.last_name || ''}
            </div>
            {trainer.specialization && (
              <div className="text-sm text-tg-hint">{trainer.specialization}</div>
            )}
            {isTrial && (
              <span className="inline-block px-2 py-0.5 bg-orange-100 text-orange-700 text-xs rounded-full mt-1">
                Пробное занятие
              </span>
            )}
          </div>
        </div>
      )}

      {/* Ошибка */}
      {error && (
        <div className="bg-red-100 text-red-700 p-3 rounded-lg mb-4">{error}</div>
      )}

      {/* Ограничения trial */}
      {!isLoading && isTrial && trialInfo && !trialInfo.can_book && (
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mb-6">
          {trialInfo.status === 'completed' ? (
            <>
              <div className="font-semibold text-orange-800 mb-1">
                Пробная тренировка завершена
              </div>
              <p className="text-orange-700 text-sm">
                Ожидайте решения тренера о дальнейшем сотрудничестве.
              </p>
            </>
          ) : trialInfo.status === 'booked' ? (
            trialInfo.booking_date && trialInfo.booking_date < format(new Date(), 'yyyy-MM-dd') ? (
              <>
                <div className="font-semibold text-orange-800 mb-1">
                  Пробная тренировка прошла
                </div>
                <p className="text-orange-700 text-sm">
                  Ожидайте решения тренера о дальнейшем сотрудничестве.
                </p>
              </>
            ) : (
              <>
                <div className="font-semibold text-orange-800 mb-1">
                  Пробная тренировка забронирована
                </div>
                <p className="text-orange-700 text-sm">
                  {trialInfo.booking_date && (
                    <>
                      {format(parseISO(trialInfo.booking_date), 'd MMMM', { locale: ru })}, {trialInfo.booking_time}
                    </>
                  )}
                </p>
                <p className="text-orange-600 text-xs mt-2">
                  Вы можете отменить запись в разделе «Мои тренировки» и перезаписаться на другое время.
                </p>
                <Link to="/my-trainings" className="inline-block mt-3">
                  <Button size="sm" variant="secondary">
                    Мои тренировки
                  </Button>
                </Link>
              </>
            )
          ) : null}
        </div>
      )}

      {/* Нет тренера */}
      {!actualTrainerId && !isLoading && (
        <div className="text-center py-8">
          <p className="text-tg-hint mb-2">У вас нет активных тренеров</p>
          <p className="text-sm text-tg-hint">
            Попросите тренера прислать вам ссылку-приглашение
          </p>
        </div>
      )}

      {/* Загрузка */}
      {isLoading && (
        <div className="text-center py-8 text-tg-hint">Загрузка расписания...</div>
      )}

      {/* Контент — выбор даты/времени (скрыт для trial с ограничениями) */}
      {!isLoading && !error && canShowSlots && (
        <>
          {/* Выбор даты */}
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-tg-text mb-3">Дата</h2>
            <div className="flex gap-2 overflow-x-auto pb-2">
              {dates.map((date) => {
                const dateStr = format(date, 'yyyy-MM-dd')
                const hasSlots = slots.some((s) => s.date === dateStr)
                const isSelected = selectedDate === dateStr

                return (
                  <button
                    key={dateStr}
                    onClick={() => {
                      setSelectedDate(dateStr)
                      setSelectedSlot(null)
                    }}
                    disabled={!hasSlots}
                    className={`flex-shrink-0 w-14 h-16 rounded-xl flex flex-col items-center justify-center transition-all ${
                      isSelected
                        ? 'bg-tg-button text-tg-button-text'
                        : hasSlots
                        ? 'bg-tg-secondary text-tg-text'
                        : 'bg-tg-secondary/50 text-tg-hint/50'
                    }`}
                  >
                    <span className="text-xs opacity-70">
                      {format(date, 'EEE', { locale: ru })}
                    </span>
                    <span className="text-lg font-medium">{format(date, 'd')}</span>
                  </button>
                )
              })}
            </div>
          </div>

          {/* Выбор времени */}
          {selectedDate && (
            <div className="mb-6 animate-fadeIn">
              <h2 className="text-lg font-semibold text-tg-text mb-3">Время</h2>
              {slotsForDate.length === 0 ? (
                <p className="text-tg-hint text-center py-4">
                  Нет доступного времени на эту дату
                </p>
              ) : (
                <div className="grid grid-cols-3 gap-2">
                  {slotsForDate.map((slot) => (
                    <button
                      key={slot.id}
                      onClick={() => setSelectedSlot(slot)}
                      className={`py-3 rounded-xl font-medium transition-all ${
                        selectedSlot?.id === slot.id
                          ? 'bg-tg-button text-tg-button-text'
                          : 'bg-tg-secondary text-tg-text'
                      }`}
                    >
                      {slot.start_time} - {slot.end_time}
                      {slot.max_clients && slot.max_clients > 1 && (
                        <span className="text-xs opacity-70 block">
                          {slot.available_spots} из {slot.max_clients} мест
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Нет слотов вообще */}
          {slots.length === 0 && (
            <div className="text-center py-8">
              <p className="text-tg-hint mb-2">
                Нет доступного времени для записи
              </p>
              <p className="text-sm text-tg-hint">
                Тренер пока не добавил слоты в расписание
              </p>
            </div>
          )}
        </>
      )}

      {/* Кнопка записи */}
      {selectedSlot && (
        <div className="fixed bottom-20 left-4 right-4 animate-slideUp">
          <Button
            onClick={handleBooking}
            fullWidth
            size="lg"
            loading={isBooking}
          >
            {isTrial ? 'Записаться на пробную тренировку' : `Записаться на ${selectedSlot.start_time}`}
          </Button>
        </div>
      )}
    </div>
  )
}
